import React, { useState, useEffect, useRef } from 'react';
import { Line, Doughnut, Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    LineElement, PointElement, LinearScale, CategoryScale,
    Title, Tooltip, Legend, ArcElement, BarElement, Filler
} from 'chart.js';
import * as Authservices from '../Services/services';

ChartJS.register(LineElement, PointElement, LinearScale, CategoryScale, Title, Tooltip, Legend, ArcElement, BarElement, Filler);

const fmt$ = (n) => `$${Number(n || 0).toLocaleString()}`;
const fmtCoins = (n) => {
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return Number(n || 0).toLocaleString();
};
const isPos = (v) => !String(v || '').startsWith('-');

const DEFAULT_LABELS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const REVENUE_DATASETS_DEF = [
    { label: 'Coin Purchases',    borderColor: '#f82581', bgColor: 'rgba(248,37,129,0.12)' },
    { label: 'VIP Subscriptions', borderColor: '#6366f1', bgColor: 'rgba(99,102,241,0.12)' },
    { label: 'Ad Revenue',        borderColor: '#10b981', bgColor: 'rgba(16,185,129,0.12)' },
];
const ENGAGE_DATASETS_DEF = [
    { label: 'Watch Time (hrs)', borderColor: '#f59e0b', bgColor: 'rgba(245,158,11,0.15)' },
    { label: 'Active Users',     borderColor: '#06b6d4', bgColor: 'rgba(6,182,212,0.15)'  },
];
const COIN_DATASETS_DEF = [
    { label: 'Coins Purchased',      borderColor: '#f82581', bgColor: 'rgba(248,37,129,0.12)' },
    { label: 'Coins Spent',          borderColor: '#f59e0b', bgColor: 'rgba(245,158,11,0.12)' },
    { label: 'Free Coins (Rewards)', borderColor: '#10b981', bgColor: 'rgba(16,185,129,0.12)' },
];

const DatasetToggle = ({ defs, visible, onToggle }) => (
    <div style={{ display:'flex', flexWrap:'wrap', gap:'8px', marginBottom:'14px' }}>
        {defs.map((ds, i) => (
            <button key={i} onClick={() => onToggle(i)} style={{
                display:'flex', alignItems:'center', gap:'7px', cursor:'pointer',
                background: visible[i] ? `${ds.borderColor}18` : 'rgba(255,255,255,0.04)',
                border: `1.5px solid ${visible[i] ? ds.borderColor : 'rgba(255,255,255,0.12)'}`,
                borderRadius:'20px', padding:'5px 13px',
                color: visible[i] ? '#f0f0f8' : '#8a8a9a',
                fontSize:'12px', fontWeight:500, transition:'all 0.2s',
            }}>
                <span style={{
                    width:'10px', height:'10px', borderRadius:'50%', flexShrink:0,
                    background: visible[i] ? ds.borderColor : 'rgba(255,255,255,0.18)',
                    boxShadow: visible[i] ? `0 0 6px ${ds.borderColor}` : 'none',
                    transition:'all 0.2s',
                }} />
                {ds.label}
            </button>
        ))}
    </div>
);

const exportCSV = (kpiCards, revData, engData, topSeriesData, topContent, coinStats, coinData, period) => {
    const lines = [];
    const esc = v => { const s = String(v ?? ''); return (s.includes(',') || s.includes('"') || s.includes('\n')) ? `"${s.replace(/"/g,'""')}"` : s; };
    const row = (...c) => lines.push(c.map(esc).join(','));
    row('DRAMAMIX ANALYTICS REPORT');
    row(`Generated: ${new Date().toLocaleString()}`, `Period: ${period}`);
    lines.push('');
    row('KPI SUMMARY');
    row('Metric','Value','Change','Direction');
    kpiCards.forEach(k => row(k.label, k.value, k.change, k.positive ? 'Up' : 'Down'));
    lines.push('');
    row('REVENUE TRENDS');
    row('Month', ...revData.datasets.map(d => d.label));
    revData.labels.forEach((lbl, i) => row(lbl, ...revData.datasets.map(d => d.data[i] ?? '')));
    lines.push('');
    row('USER ENGAGEMENT');
    row('Day', ...engData.datasets.map(d => d.label));
    engData.labels.forEach((lbl, i) => row(lbl, ...engData.datasets.map(d => d.data[i] ?? '')));
    lines.push('');
    row('TOP SERIES BY VIEWS');
    row('Series','Views (K)');
    topSeriesData.labels.forEach((lbl, i) => row(lbl, topSeriesData.datasets[0].data[i] ?? ''));
    lines.push('');
    row('TOP PERFORMING CONTENT');
    row('Rank','Title','Views','Completion Rate (%)','Revenue','Trending');
    topContent.forEach(t => row(t.rank, t.title, t.views, t.completion === 'N/A' ? 'N/A' : t.completion, t.revenue, t.trending ? 'Yes' : 'No'));
    lines.push('');
    row('COIN ECONOMY STATS');
    row('Metric','Value');
    coinStats.forEach(s => row(s.label, s.value));
    lines.push('');
    row('COIN ECONOMY FLOW');
    row('Month', ...coinData.datasets.map(d => d.label));
    coinData.labels.forEach((lbl, i) => row(lbl, ...coinData.datasets.map(d => d.data[i] ?? '')));
    const blob = new Blob([lines.join('\n')], { type:'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `analytics_report_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
};

const exportPDF = (kpiCards, revData, topContent, coinStats, period) => {
    const win = window.open('', '_blank');
    const tbl = (heads, rows) =>
        `<table><thead><tr>${heads.map(h => `<th>${h}</th>`).join('')}</tr></thead><tbody>` +
        rows.map((r, i) => `<tr class="${i%2===0?'ev':'od'}">${r.map(c => `<td>${c}</td>`).join('')}</tr>`).join('') +
        '</tbody></table>';

    const kpiRows = kpiCards.map(k => [
        k.label, `<strong>${k.value}</strong>`,
        `<span class="${k.positive?'up':'dn'}">${k.positive?'▲':'▼'} ${k.change}</span>`
    ]);
    const revRows = revData.labels.map((lbl, i) => [lbl, ...revData.datasets.map(d => {
        const v = d.data[i]; return v != null ? `$${Number(v).toLocaleString()}` : '—';
    })]);
    const contentRows = topContent.map(t => [
        `<span class="rank">${t.rank}</span>`, `<strong>${t.title}</strong>`,
        t.views,
        t.completion === 'N/A' ? '—'
            : `<div class="pb"><div class="pf" style="width:${t.completion}%"></div><span>${t.completion}%</span></div>`,
        `<span class="rev">${t.revenue}</span>`,
        t.trending ? `<span class="badge">● Trending</span>` : '—'
    ]);
    const coinRows = coinStats.map(s => [s.label, `<strong>${s.value}</strong>`]);

    win.document.write(`<!DOCTYPE html><html><head><title>Dramamix Analytics Report</title>
<style>
*{box-sizing:border-box;margin:0;padding:0}
body{font-family:'Segoe UI',Arial,sans-serif;background:#fff;color:#1a1a2e;padding:40px}
.cover{text-align:center;padding:40px 0 28px;border-bottom:3px solid #f82581;margin-bottom:30px}
.cover h1{font-size:26px;font-weight:700;color:#1a1a2e;margin-bottom:5px}
.cover .sub{font-size:14px;color:#888;margin-bottom:16px}
.cover .meta{display:flex;justify-content:center;gap:16px;flex-wrap:wrap;font-size:12px;color:#555}
.cover .meta span{background:#f8f9fa;padding:5px 14px;border-radius:20px;border:1px solid #e8e8e8}
.sec{margin-bottom:30px;page-break-inside:avoid}
.sec-title{font-size:13px;font-weight:700;color:#f82581;padding:8px 0;border-bottom:2px solid #f82581;margin-bottom:12px;text-transform:uppercase;letter-spacing:.5px}
table{width:100%;border-collapse:collapse;font-size:12.5px}
th{background:#1a1a2e;color:#fff;padding:10px 14px;text-align:left;font-weight:600}
td{padding:9px 14px;border-bottom:1px solid #eee;vertical-align:middle}
.ev td{background:#fafafa}.od td{background:#fff}
tr:hover td{background:#fff3f8}
.up{color:#10b981;font-weight:600}.dn{color:#ef4444;font-weight:600}
.rank{display:inline-flex;align-items:center;justify-content:center;width:24px;height:24px;background:#f82581;color:#fff;border-radius:50%;font-size:11px;font-weight:700}
.rev{color:#10b981;font-weight:600}
.badge{background:#fff3f8;color:#f82581;padding:3px 10px;border-radius:20px;font-size:11px;font-weight:600}
.pb{display:flex;align-items:center;gap:8px}
.pf{height:6px;background:linear-gradient(90deg,#f82581,#6366f1);border-radius:3px;min-width:4px}
.pb span{font-size:11px;color:#555;white-space:nowrap}
footer{margin-top:28px;padding-top:12px;border-top:1px solid #ddd;display:flex;justify-content:space-between;font-size:11px;color:#aaa}
@media print{body{padding:20px}}
</style></head><body>
<div class="cover">
  <h1>Analytics Report</h1>
  <div class="sub">Dramamix Admin Platform — Confidential</div>
  <div class="meta">
    <span>Period: ${period}</span>
    <span>${new Date().toLocaleDateString('en-US',{weekday:'long',year:'numeric',month:'long',day:'numeric'})}</span>
    <span>${new Date().toLocaleTimeString()}</span>
  </div>
</div>
<div class="sec"><div class="sec-title">KPI Summary</div>${tbl(['Metric','Value','Change'],kpiRows)}</div>
<div class="sec"><div class="sec-title">Revenue Trends</div>${tbl(['Month',...revData.datasets.map(d=>d.label)],revRows)}</div>
<div class="sec"><div class="sec-title">Top Performing Content</div>${tbl(['#','Series Title','Views','Completion','Revenue','Trending'],contentRows)}</div>
<div class="sec"><div class="sec-title">Coin Economy Statistics</div>${tbl(['Metric','Value'],coinRows)}</div>
<footer><span>Dramamix Admin Panel</span><span>Generated: ${new Date().toISOString()}</span></footer>
<script>window.onload=function(){window.print()}<\/script>
</body></html>`);
    win.document.close();
};

const AnalyticsDashboard = () => {
    const [period, setPeriod] = useState('30d');
    const [apiData, setApiData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [apiError, setApiError] = useState(false);

    const [visibleRev,    setVisibleRev]    = useState([true, true, true]);
    const [visibleEngage, setVisibleEngage] = useState([true, true]);
    const [visibleCoin,   setVisibleCoin]   = useState([true, true, true]);

    const toggleRev    = (i) => setVisibleRev(p    => p.map((v,j) => j===i ? !v : v));
    const toggleEngage = (i) => setVisibleEngage(p => p.map((v,j) => j===i ? !v : v));
    const toggleCoin   = (i) => setVisibleCoin(p   => p.map((v,j) => j===i ? !v : v));

    useEffect(() => {
        setLoading(true);
        setApiError(false);
        Authservices.getAnalyticsDashboard(period)
            .then(res => {
                if (res?.status === 200) {
                    setApiData(res.data?.responseDetails || res.data?.data || null);
                }
            })
            .catch(() => {
                setApiError(true);
                setApiData(null);
            })
            .finally(() => setLoading(false));
    }, [period]);

    // Map actual API response fields
    const au   = apiData?.active_users;
    const ug   = apiData?.user_growth;
    const rev  = apiData?.revenue;
    const vip  = apiData?.vip;
    const cont = apiData?.content;

    const mau          = au  ? Number(au.mau)                             : null;
    const wau          = au  ? Number(au.wau)                             : null;
    const newToday     = ug  ? Number(ug.new_today)                       : null;
    const newMonth     = ug  ? Number(ug.new_month)                       : null;
    const totalRevenue = rev ? Number(rev.summary?.total_revenue || 0)    : null;
    const totalOrders  = rev ? Number(rev.summary?.total_orders  || 0)    : null;
    const avgOrder     = rev ? Number(rev.summary?.avg_order_value || 0)  : null;
    const mrr          = rev ? Number(rev.mrr || 0)                       : null;
    const totalVip     = vip ? Number(vip.total_vip   || 0)              : null;
    const weeklyVip    = vip ? Number(vip.weekly_vip  || 0)              : null;
    const yearlyVip    = vip ? Number(vip.yearly_vip  || 0)              : null;

    const kpiCards = [
        { label:'Total Revenue',      value: totalRevenue !== null ? fmt$(totalRevenue)                        : '—', change:'—', positive:true,  icon:'fas fa-dollar-sign',    color:'#10b981' },
        { label:'Monthly Active Users',value: mau !== null          ? mau.toLocaleString()                     : '—', change:'—', positive:true,  icon:'fas fa-users',          color:'#6366f1' },
        { label:'Weekly Active Users', value: wau !== null          ? wau.toLocaleString()                     : '—', change:'—', positive:true,  icon:'fas fa-user-clock',     color:'#f59e0b' },
        { label:'New Users Today',     value: newToday !== null      ? newToday.toLocaleString()               : '—', change:'—', positive:true,  icon:'fas fa-user-plus',      color:'#f82581' },
        { label:'New Users This Month',value: newMonth !== null      ? newMonth.toLocaleString()               : '—', change:'—', positive:true,  icon:'fas fa-calendar-alt',   color:'#06b6d4' },
        { label:'Total VIP Users',     value: totalVip !== null      ? totalVip.toLocaleString()               : '—', change:'—', positive:true,  icon:'fas fa-crown',          color:'#8b5cf6' },
        { label:'Total Orders',        value: totalOrders !== null   ? totalOrders.toLocaleString()            : '—', change:'—', positive:true,  icon:'fas fa-shopping-bag',   color:'#ef4444' },
        { label:'Avg. Order Value',    value: avgOrder !== null      ? fmt$(avgOrder)                          : '—', change:'—', positive:true,  icon:'fas fa-chart-line',     color:'#ec4899' },
    ];

    // Revenue Trends — use real daily_trend if available, else fallback
    const revDailyTrend = rev?.daily_trend || [];
    const hasRevTrend = revDailyTrend.length > 0;
    const revLabels = hasRevTrend ? revDailyTrend.map(r => r.date?.slice(5)) : DEFAULT_LABELS;
    const revRawDatasets = [
        { data: hasRevTrend ? revDailyTrend.map(r => Number(r.total_revenue || r.revenue || 0))     : [12400,15200,18300,17100,21500,24800,28900,31200,29400,33100,36800,42100] },
        { data: hasRevTrend ? revDailyTrend.map(r => Number(r.vip_revenue   || 0))                   : [8200,9100,10500,11200,13800,15100,17400,19800,18600,21300,24500,28200] },
        { data: hasRevTrend ? revDailyTrend.map(r => Number(r.ad_revenue    || 0))                   : [3100,3400,4200,4800,5100,5900,6400,7100,6800,7500,8200,9100] },
    ];
    const revenueData = {
        labels: revLabels,
        datasets: REVENUE_DATASETS_DEF.map((def, i) => ({
            label: def.label, data: revRawDatasets[i].data,
            borderColor: def.borderColor, backgroundColor: def.bgColor,
            tension: 0.4, fill: true,
        })).filter((_, i) => visibleRev[i]),
    };
    const revenueDataAll = {
        labels: revLabels,
        datasets: REVENUE_DATASETS_DEF.map((def, i) => ({ label: def.label, data: revRawDatasets[i].data })),
    };

    // Revenue Breakdown — use by_plan_type if available
    const byPlan = rev?.by_plan_type || [];
    const coinRev = byPlan.find(p => p.plan_type === 'coin')?.revenue  || (hasRevTrend ? 0 : 42100);
    const vipRev  = byPlan.find(p => p.plan_type === 'vip')?.revenue   || (hasRevTrend ? 0 : 28200);
    const adRev   = byPlan.find(p => p.plan_type === 'ad')?.revenue    || (hasRevTrend ? 0 : 9100);
    const revenueBreakdown = {
        labels: ['Coin Purchases','VIP Subs','Ad Revenue'],
        datasets: [{ data: [Number(coinRev), Number(vipRev), Number(adRev)], backgroundColor:['#f82581','#6366f1','#10b981'], borderWidth:0, hoverOffset:8 }],
    };
    const revenueTotal = Number(totalRevenue ?? (42100+28200+9100));

    // User Engagement — real daily_trend for new users, fallback watch time
    const ugTrend = ug?.daily_trend || [];
    const hasUgTrend = ugTrend.length > 0;
    const engLabels = hasUgTrend ? ugTrend.map(r => r.date?.slice(5)) : ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
    const engRawDatasets = [
        { data: hasUgTrend ? ugTrend.map(r => 0)                  : [4520,5100,4800,5300,6200,8100,7800] }, // watch time not in API yet
        { data: hasUgTrend ? ugTrend.map(r => Number(r.new_users)) : [2100,2400,2300,2600,3100,4200,3900] },
    ];
    const engagementData = {
        labels: engLabels,
        datasets: ENGAGE_DATASETS_DEF.map((def, i) => ({
            label: def.label, data: engRawDatasets[i].data,
            borderColor: def.borderColor, backgroundColor: def.bgColor,
            tension: 0.4, fill: true,
        })).filter((_, i) => visibleEngage[i]),
    };
    const engagementDataAll = {
        labels: engLabels,
        datasets: ENGAGE_DATASETS_DEF.map((def, i) => ({ label: def.label, data: engRawDatasets[i].data })),
    };

    // Top Series by Views — real data from content.top_by_views
    const topByViews = cont?.top_by_views || [];
    const BAR_COLORS = ['rgba(248,37,129,0.82)','rgba(99,102,241,0.82)','rgba(16,185,129,0.82)','rgba(245,158,11,0.82)','rgba(139,92,246,0.82)','rgba(6,182,212,0.82)','rgba(236,72,153,0.82)','rgba(16,185,129,0.65)','rgba(245,158,11,0.65)','rgba(248,37,129,0.65)'];
    const hasTopViews = topByViews.length > 0;
    const topSeriesData = {
        labels: hasTopViews ? topByViews.map(s => s.title) : ['Love in Seoul','Dark Secrets','Campus Queen',"CEO's Heart",'Revenge Game','First Kiss','Bad Boy Good'],
        datasets: [{
            label: 'Views',
            data:   hasTopViews ? topByViews.map(s => Number(s.views)) : [145,128,112,98,87,76,65],
            backgroundColor: BAR_COLORS,
            borderWidth:0, borderRadius:6,
        }],
    };

    // Top Performing Content table — real data from top_by_views
    const topContent = hasTopViews
        ? topByViews.slice(0,10).map((item, i) => ({
            rank: i + 1,
            title: item.title,
            views: Number(item.views).toLocaleString(),
            completion: 'N/A',
            revenue: 'N/A',
            trending: i < 3,
          }))
        : [
            { rank:1, title:'Love in Seoul',  views:'145K', completion:78, revenue:'$12,400', trending:true },
            { rank:2, title:'Dark Secrets',    views:'128K', completion:72, revenue:'$10,800', trending:true },
            { rank:3, title:'Campus Queen',    views:'112K', completion:68, revenue:'$9,200',  trending:true },
            { rank:4, title:"CEO's Heart",     views:'98K',  completion:74, revenue:'$8,500',  trending:true },
            { rank:5, title:'Revenge Game',    views:'87K',  completion:65, revenue:'$7,100',  trending:false },
          ];

    const ce = apiData?.coin_economy?.stats;
    const coinStats = [
        { label:'Total Coins in Circulation', value: ce ? fmtCoins(ce.total_circulation)           : '—', icon:'fas fa-coins',          color:'#f59e0b' },
        { label:'Coins Purchased Today',      value: ce ? Number(ce.purchased_today).toLocaleString() : '—', icon:'fas fa-shopping-cart',  color:'#f82581' },
        { label:'Coins Spent Today',          value: ce ? Number(ce.spent_today).toLocaleString()     : '—', icon:'fas fa-fire',           color:'#ef4444' },
        { label:'Free Coins Given Today',     value: ce ? Number(ce.free_coins_today).toLocaleString(): '—', icon:'fas fa-gift',           color:'#10b981' },
        { label:'Daily Check-in Rate',        value: ce ? `${ce.checkin_rate_pct}%`                   : '—', icon:'fas fa-calendar-check', color:'#6366f1' },
        { label:'Ad Watch Completion',        value: ce ? `${ce.ad_watch_completion_pct}%`            : '—', icon:'fas fa-play-circle',    color:'#06b6d4' },
    ];

    const cf = apiData?.coin_economy?.flow;
    const coinRawDatasets = [
        { data: cf?.coins_purchased    || [520000,610000,740000,690000,850000,980000,1120000,1280000,1190000,1340000,1510000,1720000] },
        { data: cf?.coins_spent        || [480000,570000,680000,650000,790000,910000,1050000,1180000,1100000,1250000,1400000,1590000] },
        { data: cf?.free_coins_rewards || [95000,110000,130000,125000,155000,175000,200000,225000,210000,240000,270000,310000] },
    ];
    const coinEconomyData = {
        labels: cf?.labels || DEFAULT_LABELS,
        datasets: COIN_DATASETS_DEF.map((def, i) => ({
            label: def.label,
            data: coinRawDatasets[i].data,
            borderColor: def.borderColor,
            backgroundColor: def.bgColor,
            tension: 0.4, fill: true,
        })).filter((_, i) => visibleCoin[i]),
    };
    const coinEconomyDataAll = {
        labels: cf?.labels || DEFAULT_LABELS,
        datasets: COIN_DATASETS_DEF.map((def, i) => ({ label: def.label, data: coinRawDatasets[i].data })),
    };

    const chartOptions = {
        responsive: true, maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: { mode:'index', intersect:false, backgroundColor:'#1a1a2e', titleColor:'#fff', bodyColor:'#c8c8d8', borderColor:'rgba(255,255,255,0.1)', borderWidth:1 },
        },
        scales: {
            x: { ticks:{ color:'#8a8a9a' }, grid:{ color:'rgba(255,255,255,0.04)' } },
            y: { ticks:{ color:'#8a8a9a' }, grid:{ color:'rgba(255,255,255,0.04)' } },
        },
    };

    const doughnutOptions = {
        responsive: true, maintainAspectRatio: false,
        plugins: { legend:{ position:'bottom', labels:{ color:'#c8c8d8', padding:16, font:{ size:12 } } } },
        cutout:'72%',
    };

    const barOptions = {
        ...chartOptions,
        indexAxis: 'y',
        plugins: {
            ...chartOptions.plugins,
            legend: { display: false },
            tooltip: {
                mode: 'nearest',
                intersect: true,
                backgroundColor:'#1a1a2e',
                titleColor:'#fff',
                bodyColor:'#c8c8d8',
                borderColor:'rgba(255,255,255,0.1)',
                borderWidth:1,
                callbacks: {
                    label: (ctx) => ` Views: ${Number(ctx.parsed.x).toLocaleString()}`,
                },
            },
        },
    };

    const handleExportCSV = () => exportCSV(kpiCards, revenueDataAll, engagementDataAll, topSeriesData, topContent, coinStats, coinEconomyDataAll, period);
    const handleExportPDF = () => exportPDF(kpiCards, revenueDataAll, topContent, coinStats, period);

    const [showExport, setShowExport] = useState(false);

    return (
        <section className="analytics-section section">
            <div className="analytics-header">
                <div>
                    <h4>Analytics Dashboard {loading && <span style={{ fontSize:'12px', color:'#8a8a9a', marginLeft:8 }}>Loading…</span>}</h4>
                    <p className="analytics-subtitle">
                        Comprehensive insights into your platform performance
                        {apiError && <span style={{ marginLeft:10, fontSize:'11px', color:'#f59e0b' }}><i className="fas fa-exclamation-triangle" style={{ marginRight:4 }}></i>Analytics API unavailable — connect backend endpoint to show real data</span>}
                    </p>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
                    <div style={{ position:'relative', display:'inline-block' }}>
                        <button
                            className="export-trigger-btn"
                            onClick={() => setShowExport(v => !v)}
                            onBlur={() => setTimeout(() => setShowExport(false), 200)}
                        >
                            <i className="fas fa-download"></i> Export
                            <i className={`fas fa-chevron-${showExport ? 'up' : 'down'}`} style={{ marginLeft:'6px', fontSize:'10px' }}></i>
                        </button>
                        {showExport && (
                            <div className="export-dropdown">
                                <button onClick={() => { handleExportCSV(); setShowExport(false); }}><i className="fas fa-file-csv"></i> Export as CSV</button>
                                <button onClick={() => { handleExportPDF(); setShowExport(false); }}><i className="fas fa-file-pdf"></i> Export as PDF</button>
                            </div>
                        )}
                    </div>
                    <div className="analytics-period-tabs">
                        {[{k:'7d',l:'7 Days'},{k:'14d',l:'14 Days'},{k:'30d',l:'30 Days'},{k:'90d',l:'90 Days'}].map(p => (
                            <button key={p.k} className={`analytics-period-tab ${period===p.k?'active':''}`} onClick={() => setPeriod(p.k)}>{p.l}</button>
                        ))}
                    </div>
                </div>
            </div>

            <div className="analytics-kpi-grid">
                {kpiCards.map((kpi, i) => (
                    <div key={i} className="analytics-kpi-card">
                        <div className="analytics-kpi-icon" style={{ background:`${kpi.color}15`, color:kpi.color }}>
                            <i className={kpi.icon}></i>
                        </div>
                        <div className="analytics-kpi-info">
                            <span className="analytics-kpi-label">{kpi.label}</span>
                            <strong className="analytics-kpi-value">{kpi.value}</strong>
                        </div>
                        {kpi.change !== '—' && (
                            <span className={`analytics-kpi-change ${kpi.positive ? 'positive' : 'negative'}`}>
                                <i className={`fas fa-arrow-${kpi.positive ? 'up' : 'down'}`}></i> {kpi.change}
                            </span>
                        )}
                    </div>
                ))}
            </div>

            <div className="analytics-charts-row">
                <div className="analytics-chart-card wide">
                    <h6>Revenue Trends</h6>
                    <DatasetToggle defs={REVENUE_DATASETS_DEF} visible={visibleRev} onToggle={toggleRev} />
                    <div className="analytics-chart-wrap">
                        <Line data={revenueData} options={chartOptions} />
                    </div>
                </div>
                <div className="analytics-chart-card">
                    <h6>Revenue Breakdown</h6>
                    <div className="analytics-chart-wrap doughnut">
                        <Doughnut data={revenueBreakdown} options={doughnutOptions} />
                    </div>
                    <div className="analytics-revenue-total">
                        <span>Total</span>
                        <strong>{fmt$(revenueTotal)}</strong>
                    </div>
                </div>
            </div>

            <div className="analytics-charts-row">
                <div className="analytics-chart-card wide">
                    <h6>User Growth{hasUgTrend ? ` — ${period === '7d' ? 'Last 7 Days' : period === '14d' ? 'Last 14 Days' : period === '30d' ? 'Last 30 Days' : 'Last 90 Days'}` : ' (This Week)'}</h6>
                    <DatasetToggle defs={ENGAGE_DATASETS_DEF} visible={visibleEngage} onToggle={toggleEngage} />
                    <div className="analytics-chart-wrap">
                        <Line data={engagementData} options={chartOptions} />
                    </div>
                </div>
                <div className="analytics-chart-card">
                    <h6>Top Series by Views</h6>
                    <div className="analytics-chart-wrap" style={{ height:'300px' }}>
                        <Bar data={topSeriesData} options={barOptions} />
                    </div>
                </div>
            </div>

            <div className="analytics-section-title">
                <h5><i className="fas fa-fire" style={{ color:'#f82581' }}></i> Top Performing Content</h5>
            </div>
            <div className="analytics-content-table-wrap">
                <table className="analytics-content-table">
                    <thead>
                        <tr><th>#</th><th>Series</th><th>Views</th><th>Completion Rate</th><th>Revenue</th><th>Status</th></tr>
                    </thead>
                    <tbody>
                        {topContent.map(item => (
                            <tr key={item.rank}>
                                <td><span className="analytics-rank">{item.rank}</span></td>
                                <td><strong>{item.title}</strong></td>
                                <td>{item.views}</td>
                                <td>
                                    {item.completion === 'N/A'
                                        ? <span style={{ color:'#8a8a9a', fontSize:12 }}>N/A</span>
                                        : <div className="analytics-progress-bar">
                                            <div>
                                                <div className="analytics-progress-fill" style={{ width:`${item.completion}%` }}></div>
                                            </div>
                                            <span>{item.completion}%</span>
                                          </div>
                                    }
                                </td>
                                <td className="analytics-revenue">{item.revenue}</td>
                                <td>
                                    {item.trending
                                        ? <span className="analytics-trending-badge"><i className="fas fa-arrow-trend-up"></i> Trending</span>
                                        : <span style={{ color:'#8a8a9a', fontSize:12 }}>Stable</span>
                                    }
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="analytics-section-title">
                <h5><i className="fas fa-coins" style={{ color:'#f59e0b' }}></i> Coin Economy</h5>
            </div>
            <div className="analytics-coin-stats">
                {coinStats.map((stat, i) => (
                    <div key={i} className="analytics-coin-stat-card">
                        <div className="analytics-coin-icon" style={{ background:`${stat.color}15`, color:stat.color }}>
                            <i className={stat.icon}></i>
                        </div>
                        <span className="analytics-coin-label">{stat.label}</span>
                        <strong className="analytics-coin-value">{stat.value}</strong>
                    </div>
                ))}
            </div>
            <div className="analytics-chart-card" style={{ marginTop:'20px' }}>
                <h6>Coin Economy Flow</h6>
                <DatasetToggle defs={COIN_DATASETS_DEF} visible={visibleCoin} onToggle={toggleCoin} />
                <div className="analytics-chart-wrap">
                    <Line data={coinEconomyData} options={chartOptions} />
                </div>
            </div>
        </section>
    );
};

export default AnalyticsDashboard;
